
import { defineComponent, onMounted, Ref, ref, toValue, computed } from 'vue';
import SPage from 'PortalDir/components/page/SPage.vue';
import SSelect from 'SharedComponents/ui/Select/SSelect.vue';
import NotFoundData from 'SharedComponents/ui/Images/NotFoundData.vue';
import Filters from 'PortalDir/components/Filters.vue';
import { usePublicServicesList } from 'PortalDir/modules/Services/composables/usePublicServicesList';
import ServiceItem from 'PortalDir/modules/Services/components/Item.vue';
import { preFetch } from 'quasar/wrappers';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { CatalogsApi, PublicServiceApi, ServiceGroupsApi } from 'SharedDir/backend/api';
import { paramsAllRequest } from 'SharedDir/backend/api/default-params';
import { Catalogs } from 'SharedDir/declarations/catalogs';
import { ServiceGroupFullBrief } from 'SharedDir/declarations/service-groups';
import { useDeviceSize } from 'SharedDir/common/composables/useDeviceSize';
import { useServicesStore } from 'stores/services';
import { PaginationParams } from 'SharedDir/declarations/pagination';
import { useLoadingSsr } from 'PortalDir/common/composables/useLoadingSsr';
import ServiceSkeletonItem from 'SharedDir/modules/Services/components/skeletons/SkeletonItem.vue';
import { random } from '@packages/toolbox';
import { useI18n } from 'vue-i18n';
import { FeatureFlagName } from 'SharedDir/declarations/system/region-customization';
import { useFeatureFlags } from 'SharedDir/common/composables/useFeatureFlags';
import { useMeta } from 'quasar';
import { useMetaPortal } from 'SharedDir/common/composables/useMetaPortal';

async function getData(serviceStore, ssrContext, queryParam = {}) {
  const { paginationParams, filterParams, fillFromQueryStringSsr } = usePublicServicesList();

  fillFromQueryStringSsr(queryParam, paginationParams, filterParams);

  const params = {
    ...serviceStore.pagination,
    ...paginationParams.value,
    ...serviceStore.filters,
    ...filterParams.value,
  };
  const response = await PublicServiceApi.getPaginated(params);
  const updatedPaginationParams: PaginationParams<any> = {
    page: response.page,
    rowsPerPage: response.rowsPerPage,
    rowsNumber: response.rowsNumber,
    showDeleted: response.showDeleted,
    sortBy: response.sortBy,
    descending: response.descending,
  };
  serviceStore.setServiceList(response.data);
  serviceStore.setServicePagination(updatedPaginationParams);
}

export default defineComponent({
  name: 'ServicePublicList',
  methods: { random },
  components: { ServiceSkeletonItem, ServiceItem, Filters, SPage, SSelect, NotFoundData },
  props: {},

  preFetch: preFetch(async ({ store, ssrContext, currentRoute }) => {
    const serviceStore = useServicesStore();
    await getData(serviceStore, ssrContext, currentRoute?.query || {});
  }),

  setup() {
    const { isLoading, showLoading, loadingStore } = useLoadingSsr();
    const {
      paginationParams,
      filterParams,
      fetch,
      list,
      defaultFilterParams,
      setQueryParams,
      fillFromQueryString,
    } = usePublicServicesList();
    const serviceStore = useServicesStore();
    const { t } = useI18n();
    const { getFeatureFlag } = useFeatureFlags();

    const serviceList = computed(() => {
      return serviceStore.serviceList;
    });

    const pagination = computed(() => {
      return serviceStore.pagination;
    });

    async function applyFilter() {
      await setQueryParams({ ...filterParams.value, ...pagination.value });
      paginationParams.value = { ...pagination.value };
      await fetch();
      serviceStore.setServiceList(list.value);
    }
    async function proxyInputPagination() {
      await applyFilter();

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    const maxPagePagination = computed(() => {
      return Math.ceil((pagination.value?.rowsNumber || 1) / (pagination.value?.rowsPerPage || 1));
    });

    const listMunicipal: Ref<Catalogs.CatalogsValuesFullList> = ref([]);

    const listFormSupport: Ref<Catalogs.CatalogsValuesFullList> = ref([]);

    const listKind: Ref<Catalogs.CatalogsValuesFullList> = ref([]);

    const listSegment: Ref<Catalogs.CatalogsValuesFullList> = ref([]);

    const listServiceGroups: Ref<Array<ServiceGroupFullBrief>> = ref([]);

    const getCatalogsData = async (): Promise<void> => {
      [
        { values: listMunicipal.value },
        { values: listFormSupport.value },
        { values: listKind.value },
        { values: listSegment.value },
        listServiceGroups.value,
      ] = await makeRequest(() =>
        Promise.all([
          CatalogsApi.ValuesApi('municipal').getAllValues(),
          CatalogsApi.ValuesApi('form').getAllValues(),
          CatalogsApi.ValuesApi('kind').getAllValues(),
          // { values: [] },
          CatalogsApi.ValuesApi('segments').getAllValues(),
          ServiceGroupsApi.getAllPublicList(paramsAllRequest),
        ])
      );
    };

    onMounted(async () => {
      fillFromQueryString(pagination, filterParams);
      await setQueryParams({ ...filterParams.value, ...pagination.value });
      await getCatalogsData();
      isLoading.value = false;
    });

    const { clearFullPageUrl } = useMetaPortal();

    useMeta(() => {
      return {
        title: `Меры поддержки и услуги - ${t('systemType')} ${t('systemShortName')}`.slice(0, 160),
        meta: {
          description: {
            name: 'description',
            template: () =>
              `Меры поддержки и услуги ${t('systemFullName')} (${t('systemShortName')})`,
          },
          keywords: {
            name: 'keywords',
            template: () =>
              [
                `${t('systemType')} ${t('systemShortName')}`,
                `${t('systemType')} ${t('systemFullName')}`,
                `Меры поддержки и услуги ${t('systemShortName')}`,
                `Меры поддержки и услуги ${t('systemFullName')}`,
                `Меры поддержки и услуги ${t('regionShortName')}`,
                `Меры поддержки и услуги ${t('regionFullName')}`,
              ].join(', '),
          },
        },
        link: {
          canonical: {
            rel: 'canonical',
            href: clearFullPageUrl.value,
          },
        },
      };
    });

    return {
      applyFilter,
      list,
      paginationParams,
      filterParams,
      defaultFilterParams,
      proxyInputPagination,
      pagination,
      serviceList,
      listMunicipal,
      listFormSupport,
      listKind,
      listSegment,
      listServiceGroups,
      ...useDeviceSize(),
      isLoading,
      showLoading,
      maxPagePagination,

      t,
      isVisibleServiceKindId: getFeatureFlag(FeatureFlagName.IsVisibleServiceKindId),
      isVisibleSegments: getFeatureFlag(FeatureFlagName.IsVisibleServiceSegments),
    };
  },
});
